<template>
  <div v-if="show" class="modal-overlay" @click="handleModalClick">
    <div class="modal-content" @click.stop>
      <button class="close-button" @click="handleClose">
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path d="M4.16504 4.16602L15.8309 15.8319" stroke="#5A5C62" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
          <path d="M4.1642 15.8319L15.8301 4.16602" stroke="#5A5C62" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
      </button>

      <div class="modal-header" style="display: flex; flex-direction: column; align-items: center; gap: 12px; text-align: center">
        <h2 class="modal-title" style="margin: 0">{{ $t('documentHistory.title') }}</h2>

        <div class="document-info" style="display: flex; justify-content: space-between; align-items: center; width: 100%; padding: 0 20px">
          <div class="info-row" style="flex: 1; display: flex; gap: 6px">
            <span class="info-label" style="min-width: 140px; text-align: right">{{ $t('documentHistory.filename') }}:</span>
            <div class="tooltip-container info-value" style="flex: 1">
              <span class="truncate-text">{{ selectedResultDocument.fileName }}</span>
              <div class="tooltip">{{ selectedResultDocument.fileName }}</div>
            </div>
          </div>

          <div class="info-row" style="flex: 1; display: flex; gap: 6px; justify-content: flex-end">
            <span class="info-label" style="min-width: 160px; text-align: right">{{ $t('documentHistory.resultDocument') }}:</span>
            <div class="tooltip-container info-value" style="flex: 1; max-width: 300px">
              <span class="truncate-text">{{ selectedResultDocument.resultDocumentName }}</span>
              <div class="tooltip">{{ selectedResultDocument.resultDocumentName }}</div>
            </div>
          </div>
        </div>
      </div>

      <div class="modal-body">
        <div v-if="loading" class="loading-container">
          <div class="spinner-border text-primary spinner-grow-xl" role="status"></div>
        </div>

        <div v-else class="table-container">
          <table class="custom-table">
            <thead class="custom-table-header">
              <tr class="custom-table-row">
                <th style="width: 170px; min-width: 170px">{{ $t('documentHistory.table.action_date') }}</th>
                <th style="width: 170px; min-width: 170px">{{ $t('documentHistory.table.action') }}</th>
                <th style="width: 650px; min-width: 550px">{{ $t('documentHistory.table.action_details') }}</th>
                <th style="width: 170px; min-width: 170px">{{ $t('documentHistory.table.action_by') }}</th>
              </tr>
            </thead>
            <tbody class="custom-table-body">
              <tr v-if="documentHistory.length === 0" class="no-data-row">
                <td colspan="4" class="no-data-cell">
                  <div class="no-data-message">
                    <h3>{{ $t('documentHistory.no_data.title') }}</h3>
                    <p>{{ $t('documentHistory.no_data.message') }}</p>
                  </div>
                </td>
              </tr>
              <tr v-for="(history, index) in paginatedHistory" :key="history.id || index" class="history-row">
                <td class="date-cell">
                  {{ formatDate(history.created_at) }}
                </td>
                <td class="action-cell">
                  <div class="tooltip-container">
                    <span class="truncate-text">{{ history.action_type }}</span>
                    <div class="tooltip">{{ history.action_type }}</div>
                  </div>
                </td>
                <td class="details-cell">
                  <div class="tooltip-container">
                    <span class="truncate-text">{{ history.message || '---' }}</span>
                    <div class="tooltip">{{ history.message || '---' }}</div>
                  </div>
                </td>
                <td class="user-cell">
                  <div class="tooltip-container">
                    <span class="truncate-text">{{ history.performed_by_email || '---' }}</span>
                    <div class="tooltip">{{ history.performed_by_email || '---' }}</div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="pagination-container" v-if="documentHistory.length > 0">
        <div class="pagination-controls">
          <button class="pagination-button" @click="previousPage" :disabled="currentPage === 1">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M15 18L9 12L15 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </button>
          <span class="page-info">{{ $t('documentHistory.pagination.page') }} {{ currentPage }} {{ $t('documentHistory.pagination.of') }} {{ totalPages }}</span>
          <button class="pagination-button" @click="nextPage" :disabled="currentPage === totalPages">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M9 6L15 12L9 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </button>
        </div>
      </div>

      <div class="modal-footer">
        <div class="action-buttons">
          <button class="export-button" @click="exportToCSV" :disabled="documentHistory.length === 0">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
              <polyline points="7,10 12,15 17,10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
              <line x1="12" y1="15" x2="12" y2="3" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
            {{ $t('documentHistory.buttons.export_csv') }}
          </button>
          <button class="export-button" @click="exportToPDF" :disabled="documentHistory.length === 0">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
              <polyline points="7,10 12,15 17,10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
              <line x1="12" y1="15" x2="12" y2="3" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
            {{ $t('documentHistory.buttons.export_pdf') }}
          </button>
          <button class="close-btn" @click="handleClose">
            {{ $t('documentHistory.buttons.close') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { get } from '../../utils/api.js'
import { showToast } from '../../utils/toast.js'

export default {
  name: 'DocumentHistory',
  props: {
    show: {
      type: Boolean,
      required: true,
    },
    jobId: {
      type: [String, Number],
      required: true,
    },
    selectedResultDocument: {
      fileName: { type: String, required: true },
      resultDocumentName: { type: String, required: true },
    },
  },
  data() {
    return {
      loading: false,
      documentHistory: [],
      allDocumentHistory: [], // Store all fetched data for UI pagination
      currentPage: 1,
      itemsPerPage: 10,
      pageSize: 100000, // Fetch 1 lakh records at once
    }
  },
  computed: {
    totalPages() {
      return Math.ceil(this.documentHistory.length / this.itemsPerPage)
    },
    paginatedHistory() {
      const start = (this.currentPage - 1) * this.itemsPerPage
      const end = start + this.itemsPerPage
      return this.documentHistory.slice(start, end)
    },
  },
  created() {
    // Get user data from localStorage
    const userDataString = localStorage.getItem('UserData')

    if (userDataString) {
      this.userData = JSON.parse(userDataString)
      // Initialize tenant store with user's tenant ID
    }
  },
  watch: {
    show(newVal) {
      if (newVal && this.jobId && this.selectedResultDocument) {
        this.fetchDocumentHistory()
        this.currentPage = 1
      }
    },
  },
  methods: {
    handleClose() {
      this.$emit('close')
    },
    handleModalClick(event) {
      if (event.target.classList.contains('modal-overlay')) {
        this.handleClose()
      }
    },
    async fetchDocumentHistory() {
      this.loading = true
      try {
        const params = {
          page: 1, // Always fetch from page 1 since we're getting all data
          pageSize: this.pageSize,
          jobId: this.jobId, // Filter by jobId from props
          resultDocument: this.selectedResultDocument.resultDocumentName,
        }

        // Remove empty filter values
        Object.keys(params).forEach((key) => {
          if (params[key] === '' || params[key] === null || params[key] === undefined) {
            delete params[key]
          }
        })

        const queryString = new URLSearchParams(params).toString()
        const response = await get(`/v1/mx_audit_report_log?${queryString}`)

        if (response?.data) {
          this.allDocumentHistory = response.data.data || []
          this.documentHistory = this.allDocumentHistory
        } else {
          this.documentHistory = []
          this.allDocumentHistory = []
          showToast({
            title: this.$t('documentHistory.messages.error.title'),
            message: response?.message || this.$t('documentHistory.messages.error.fetch_failed'),
            type: 'error',
          })
        }
      } catch (error) {
        this.documentHistory = []
        this.allDocumentHistory = []
        showToast({
          title: this.$t('documentHistory.messages.error.title'),
          message: error.response?.data?.message || this.$t('documentHistory.messages.error.fetch_failed'),
          type: 'error',
        })
      } finally {
        this.loading = false
      }
    },
    formatDate(dateStr) {
      if (!dateStr) return '---'
      const dateObj = new Date(dateStr)
      const dateOptions = {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
      }
      const timeOptions = {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      }
      const date = dateObj.toLocaleDateString('en-US', dateOptions)
      const time = dateObj.toLocaleTimeString('en-US', timeOptions)
      return `${date} ${time}`
    },
    previousPage() {
      if (this.currentPage > 1) {
        this.currentPage--
      }
    },
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++
      }
    },
    exportToCSV() {
      if (this.documentHistory.length === 0) return

      const headers = [this.$t('documentHistory.table.action_date'), this.$t('documentHistory.table.action'), this.$t('documentHistory.table.action_details'), this.$t('documentHistory.table.action_by')]

      const csvContent = [headers.join(','), ...this.documentHistory.map((history) => [`"${this.formatDate(history.created_at)}"`, `"${history.action_type || ''}"`, `"${history.message || ''}"`, `"${history.performed_by_email || ''}"`].join(','))].join('\n')

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      const url = URL.createObjectURL(blob)
      link.setAttribute('href', url)
      link.setAttribute('download', `document_history_job_${this.jobId}.csv`)
      link.style.visibility = 'hidden'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      showToast({
        title: this.$t('documentHistory.messages.success.title'),
        message: this.$t('documentHistory.messages.success.csv_export_success'),
        type: 'success',
      })
    },
    exportToPDF() {
      if (this.documentHistory.length === 0) return

      const logoBase64 = '/footer-logo_vt.png' // Replace with full base64

      const generatedOn = new Date().toLocaleString()

      const htmlContent = `
    <html>
      <head>
        <title>Document History - Job ${this.jobId}</title>
        <style>
          @media print {
            @page {
              margin: 0.5in;
              size: A4;
            }
            body {
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
          }
          
          body { 
            font-family: Arial, sans-serif; 
            margin: 20px; 
            background: white;
          }
          
          .header-container {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            margin-bottom: 20px;
            padding-bottom: 15px;
            border-bottom: 2px solid #164863;
          }
          
          .logo {
            height: 80px;
            width: auto;
            max-width: 200px;
          }
          
          .header-info {
            text-align: right;
            flex: 1;
            margin-left: 20px;
          }
          
          .document-info {
            margin-bottom: 10px;
          }
          
          .document-info h2 {
            margin: 0 0 5px 0;
            font-size: 18px;
            color: #164863;
            font-weight: bold;
          }
          
          .document-info p {
            margin: 2px 0;
            font-size: 12px;
            color: #555;
          }
          
          .generated-info {
            font-size: 11px;
            color: #666;
            border-top: 1px solid #ddd;
            padding-top: 8px;
            margin-top: 8px;
          }
          
          .generated-info p {
            margin: 2px 0;
          }
          
          h1 { 
            color: #164863; 
            text-align: center; 
            margin-bottom: 20px;
            font-size: 24px;
            font-weight: bold;
          }
          
          table { 
            width: 100%; 
            border-collapse: collapse; 
            margin-top: 20px; 
            font-size: 10px;
          }
          
          th, td { 
            border: 1px solid #ddd; 
            padding: 8px; 
            text-align: left; 
            word-wrap: break-word;
          }
          
          th { 
            background-color: #f8f9fa; 
            font-weight: bold; 
            color: #164863;
            font-size: 11px;
          }
          
          tr:nth-child(even) { 
            background-color: #f9f9f9; 
          }
          
          .date-column { width: 18%; }
          .action-column { width: 18%; }
          .details-column { width: 44%; }
          .user-column { width: 20%; }
          
          .page-break {
            page-break-before: always;
          }
        </style>
      </head>
      <body>
        <div class="header-container">
          <img src="${logoBase64}" class="logo" alt="Company Logo" />
          <div class="header-info">
            <div class="document-info">
              <h2>Document History Report</h2>
              <p><strong>Job ID:</strong> ${this.jobId}</p>
              <p><strong>Document Name:</strong> ${this.selectedResultDocument.resultDocumentName}</p>
              <p><strong>File Name:</strong> ${this.selectedResultDocument.fileName}</p>
            </div>
            <div class="generated-info">
              <p><strong>Generated on:</strong> ${generatedOn}</p>
              <p><strong>Exported by:</strong> ${this.userData?.email_id || '---'}</p>
              <p><strong>Total Records:</strong> ${this.documentHistory?.length}</p>
            </div>
          </div>
        </div>
        
        <table>
          <thead>
            <tr>
              <th class="date-column">${this.$t('documentHistory.table.action_date')}</th>
              <th class="action-column">${this.$t('documentHistory.table.action')}</th>
              <th class="details-column">${this.$t('documentHistory.table.action_details')}</th>
              <th class="user-column">${this.$t('documentHistory.table.action_by')}</th>
            </tr>
          </thead>
          <tbody>
            ${this.documentHistory
              .map(
                (history) => `
              <tr>
                <td>${this.formatDate(history.created_at)}</td>
                <td>${history.action_type || '---'}</td>
                <td>${history.message || '---'}</td>
                <td>${history.performed_by_email || '---'}</td>
              </tr>
            `
              )
              .join('')}
          </tbody>
        </table>
      </body>
    </html>
  `

      // Create a new window for printing
      const printWindow = window.open('', '_blank')

      // Write the content and handle the printing process
      printWindow.document.open()
      printWindow.document.write(htmlContent)
      printWindow.document.close()

      // Wait for the content to load before printing
      printWindow.onload = function () {
        printWindow.focus()
        printWindow.print()
      }

      showToast({
        title: this.$t('documentHistory.messages.success.title'),
        message: this.$t('documentHistory.messages.success.pdf_export_success'),
        type: 'success',
      })
    },
  },
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100vw;
  height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 99999;
  background: rgba(0, 0, 0, 0.5);
}

.modal-content {
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  position: relative;
  width: 90%;
  max-width: 100%;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  z-index: 100000;
  isolation: isolate;
  margin: auto;
}

.close-button {
  position: absolute;
  right: 16px;
  top: 16px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: background-color 0.2s;
  z-index: 1;
}

.close-button:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

.modal-header {
  padding: 16px 24px;
  border-bottom: 1px solid #e8e8e8;
}

.modal-title {
  color: var(--primary-color);
  font-family: Archivo, sans-serif;
  font-size: 22px;
  font-weight: 600;
  margin: 0 0 16px 0;
  text-align: center;
}

.document-info {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 12px;
}

.info-row {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 20px;
}

.info-label {
  font-family: Archivo, sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: var(--primary-color);
  min-width: 120px;
  flex-shrink: 0;
}

.info-value {
  flex: 1;
  min-width: 0;
}

.info-value .truncate-text {
  font-family: Archivo, sans-serif;
  font-size: 14px;
  color: #32393e;
  font-weight: 400;
}

.modal-body {
  padding: 12px;
  flex: 1;
  overflow-y: auto;
}

.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 300px;
}

.table-container {
  overflow-x: auto;
  position: relative;
}

.custom-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}

.custom-table-header {
  background-color: #f8f9fa;
}

.custom-table-row th {
  padding: 6px;
  text-align: left;
  font-weight: 600;
  color: var(--primary-color);
  border-bottom: 1px solid #e8e8e8;
  white-space: nowrap;
  font-size: 14px;
  font-family: Archivo, sans-serif;
}

.custom-table-body td {
  padding: 6px;
  border-bottom: 1px solid #e8e8e8;
  font-family: Archivo, sans-serif;
  font-size: 14px;
  vertical-align: middle;
}

.history-row:hover {
  background-color: rgba(22, 72, 99, 0.05);
}

.tooltip-container {
  position: relative;
  width: 100%;
  display: block;
}

.tooltip-container:hover .tooltip {
  display: block;
  visibility: visible;
  opacity: 1;
}

.tooltip {
  display: none;
  position: fixed;
  background-color: #333;
  color: white;
  padding: 8px 12px;
  border-radius: 4px;
  font-size: 12px;
  z-index: 999999;
  opacity: 0;
  transition: opacity 0.2s;
  white-space: normal;
  width: max-content;
  max-width: 300px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  word-break: break-word;
  pointer-events: none;
}

.truncate-text {
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: default;
  width: 100%;
}

.date-cell {
  /* width: 200px;
  min-width: 200px; */
}

.action-cell {
  /* width: 150px;
  min-width: 150px; */
}

.details-cell {
  /* width: auto;
  min-width: 250px; */
}

.user-cell {
  /* width: 150px;
  min-width: 150px; */
}

.no-data-row {
  background-color: #f9f9f9;
}

.no-data-cell {
  text-align: center;
  padding: 60px 20px;
}

.no-data-message h3 {
  color: var(--primary-color);
  font-size: 18px;
  margin-bottom: 8px;
  font-family: Archivo, sans-serif;
}

.no-data-message p {
  color: #666;
  font-size: 14px;
  margin: 0;
  font-family: Archivo, sans-serif;
}

.modal-footer {
  padding: 16px 24px;
  border-top: 1px solid #e8e8e8;
  background: white;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
}

.pagination-container {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 6px;
  border-top: 1px solid #e8e8e8;
  background: white;
  position: sticky;
  bottom: 0;
  z-index: 1;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 16px;
}

.pagination-button {
  background: none;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  padding: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.pagination-button:hover:not(:disabled) {
  background-color: #f5f5f5;
  border-color: var(--primary-color);
}

.pagination-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.pagination-button svg {
  width: 20px;
  height: 20px;
  color: var(--primary-color);
}

.page-info {
  font-family: Archivo, sans-serif;
  font-size: 11px;
  color: #32393e;
  min-width: 100px;
  text-align: center;
}

.action-buttons {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.export-button {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: var(--primary-color);
  color: white;
  border: none;
  border-radius: 6px;
  font-family: Archivo, sans-serif;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.export-button:hover:not(:disabled) {
  background: #0f3a4f;
  transform: translateY(-1px);
}

.export-button:disabled {
  background: #ccc;
  cursor: not-allowed;
  transform: none;
}

.export-button svg {
  width: 16px;
  height: 16px;
}

.close-btn {
  padding: 10px 20px;
  background: #6c757d;
  color: white;
  border: none;
  border-radius: 6px;
  font-family: Archivo, sans-serif;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.close-btn:hover {
  background: #5a6268;
  transform: translateY(-1px);
}

@media (max-width: 768px) {
  .modal-content {
    width: 95%;
    max-height: 95vh;
  }

  .modal-header {
    padding: 12px 16px;
  }

  .modal-title {
    font-size: 18px;
    margin-bottom: 12px;
  }

  .info-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }

  .info-label {
    min-width: auto;
    font-size: 13px;
  }

  .info-value .truncate-text {
    font-size: 13px;
  }

  .modal-footer {
    flex-direction: column;
    align-items: center;
    gap: 12px;
  }

  .action-buttons {
    justify-content: center;
    flex-wrap: wrap;
  }
}
</style>
